import {ControlValueAccessor} from "@angular/forms";
import {Directive, HostListener, Input} from "@angular/core";
import {ElementRef} from "@angular/core";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {DecimalPipe} from "@angular/common";

// ----------------------------------------------------------------------------------- //
// ----------------------------------------------------------------------------------- //

var noop = () => {
  // ...
};

@Directive({
  selector: "input[type=text][ngModel][ngNumberInput]",
  // By overriding the NG_VALUE_ACCESSOR dependency-injection token at this level of
  // the component tree / hierarchical injectors, we are effectively replacing the
  // DefaultValueAccessor for THIS INPUT ELEMENT CONTEXT. As such, when Angular looks
  // for a ControlValueAccessor implementation in the local dependency-injection
  // container, it will only find this one.
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: NgNumberInputDirective,
      multi: true
      // NOTE: I _believe_ that because I am using Ahead-of-Time (AoT) compiling in
      // this demo, I don't need to use the forwardRef() wrapper to reference the
      // Class that hasn't been defined yet.
    }
  ]
})
export class NgNumberInputDirective implements ControlValueAccessor {
  @Input() allowDecimals: boolean = true;
  @Input() allowSign: boolean = false;
  @Input() decimalPlaces?: number;

  @Input()
  format: string;

  private previousValue: string;
  private input: HTMLInputElement;
  private onChangeCallback: Function;
  private onTouchedCallback: Function;

  // --------------------------------------
  //  Regular expressions
  integerUnsigned: string = '^[0-9]*$';
  integerSigned: string = '^-?[0-9]+$';
  decimalUnsigned: string = '^[0-9]+((,|.)[0-9]+)?$';
  decimalSigned: string = '^-?[0-9]+((,|.)[0-9]+)?$';

  // I initialize the ng-model-suggestions value accessor and directive.

  constructor(elementRef: ElementRef, public decimalPipe: DecimalPipe) {

    this.input = (elementRef.nativeElement as HTMLInputElement)

    // CAUTION: These will be called by Angular when rendering the View.
    this.onChangeCallback = noop;
    this.onTouchedCallback = noop;

    this.input.classList.add('number-input');
    //this.input.value = this.decimalPipe.transform(this.mesNumberInput, this.getFormat());
  }

  // ---
  // PUBLIC METHODS.
  // ---

  // I handle the blur event on the Input element.
  @HostListener('blur')
  public handleBlur(): void {
    if (!this.input.readOnly) {
      this.displayAsNumber(this.input.value);
    }
    this.onTouchedCallback();
  }

  /**
   * Event handler for host's paste event
   * @param e
   */
  @HostListener('paste', ['$event'])
  public onPaste(e) {
    // get and validate data from clipboard
    let value = e.clipboardData.getData('text/plain');

    const selection = this.input.selectionStart;
    if (selection > 0) {
      const original = this.input.value;
      const start = original.substring(0, selection);
      const end = original.substring(selection);
      value = start + value + end;
    }
    this.displayAsNumber(value);
    e.preventDefault();
  }

  @HostListener('focus', ['$event'])
  public cleanUp(event: Event) {
    if (!this.input.readOnly) {
      if (this.input.value) {
        this.input.value = this.input.value.replace(/\s/g, '');
      }
    }
  }


  // I handle the input event on the Input element.
  @HostListener('keydown', ['$event'])
  public handleInput(e: KeyboardEvent): void {
    let cursorPosition: number = e.target['selectionStart'];
    let originalValue: string = e.target['value'];
    let key: string = this.getName(e);
    let controlOrCommand = (e.ctrlKey === true || e.metaKey === true);
    let signExists = originalValue.includes('-');
    let separatorExists = originalValue.includes(',') || originalValue.includes('.');

    // allowed keys apart from numeric characters
    let allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Escape', 'Tab'
    ];

    // when decimals are allowed, add
    // decimal separator to allowed codes when
    // its position is not close to the the sign (-. and .-)
    let separatorIsCloseToSign = (signExists && cursorPosition <= 1);
    if (this.allowDecimals && !separatorIsCloseToSign && !separatorExists) {
      allowedKeys.push(...['.', ',']);
    }

    // when minus sign is allowed, add its
    // key to allowed key only when the
    // cursor is in the first position, and
    // first character is different from
    // decimal separator
    let firstCharacterIsSeparator = (originalValue.charAt(0) != '.') || (originalValue.charAt(0) != ',');
    if (this.allowSign && !signExists && firstCharacterIsSeparator && cursorPosition == 0) {
      allowedKeys.push('-');
    }

    // allow some non-numeric characters
    if (allowedKeys.indexOf(key) != -1 ||
      // Allow: Ctrl+A and Command+A
      (key == 'a' && controlOrCommand) ||
      // Allow: Ctrl+C and Command+C
      (key == 'c' && controlOrCommand) ||
      // Allow: Ctrl+V and Command+V
      (key == 'v' && controlOrCommand) ||
      // Allow: Ctrl+X and Command+X
      (key == 'x' && controlOrCommand)) {
      // let it happen, don't do anything
      return;
    }

    // save value before keydown event
    this.previousValue = originalValue;
    // allow number characters only
    let isNumber = (new RegExp(this.integerUnsigned)).test(key);
    if (isNumber) return; else e.preventDefault();
  }
  @HostListener('keyup')
  public handleKeyup(): void {
    const numberValue = this.stringToNumber(this.input.value);
    this.onChangeCallback(numberValue);
  }

  // I register the callback to be invoked when the value of the text Input element has
  // been changed by the user.
  public registerOnChange(callback: Function): void {
    this.onChangeCallback = callback;
  }


  // I register the callback to be invoked when the text Input element has been
  // "touched" by the user.
  public registerOnTouched(callback: Function): void {
    this.onTouchedCallback = callback;
  }


  // I set the disabled property of the text Input element.
  public setDisabledState(isDisabled: boolean): void {
    this.input.disabled = isDisabled;
  }


  // I set the value property of the text Input element.
  public writeValue(value: string | number): void {
    const numberValue = this.stringToNumber(value);
    if (isNaN(numberValue)) {
      this.input.value = '';
    } else {
      this.input.value = this.decimalPipe.transform(numberValue, this.getFormat());
    }
  }

  /**
   * Get key's name
   * @param e
   */
  getName(e): string {
    if (e.key) {
      return e.key;
    } else {
      // for old browsers
      if (e.keyCode && String.fromCharCode) {

        switch (e.keyCode) {
          case   8:
            return 'Backspace';
          case   9:
            return 'Tab';
          case  27:
            return 'Escape';
          case  37:
            return 'ArrowLeft';
          case  39:
            return 'ArrowRight';
          case 188:
            return ',';
          case 190:
            return '.';
          case 109:
            return '-'; // minus in numbpad
          case 173:
            return '-'; // minus in alphabet keyboard in firefox
          case 189:
            return '-'; // minus in alphabet keyboard in chrome
          default:
            return String.fromCharCode(e.keyCode);
        }
      }
    }
  }

  displayAsNumber(value: string | number) {
    // choose the appropiate regular expression
    let regex: string;
    if (!this.allowDecimals && !this.allowSign) regex = this.integerUnsigned;
    if (!this.allowDecimals && this.allowSign) regex = this.integerSigned;
    if (this.allowDecimals && !this.allowSign) regex = this.decimalUnsigned;
    if (this.allowDecimals && this.allowSign) regex = this.decimalSigned;

    if (typeof value === "string") {
      value = value.replace(/\s/g, '').replace(/\./g, ',');

      // when a numbers begins with a decimal separator,
      // fix it adding a zero in the beginning
      let firstCharacter = value.charAt(0);
      if (firstCharacter == '.' || firstCharacter == ',')
        value = 0 + value;

      // when a numbers ends with a decimal separator,
      // fix it adding a zero in the end
      let lastCharacter = value.charAt(value.length - 1);
      if (lastCharacter == '.' || lastCharacter == ',')
        value = value + 0;

      // test number with regular expression, when
      // number is invalid, replace it with a zero
      let valid: boolean = (new RegExp(regex)).test(value);
      value = valid ? value : null;
    }

    this.writeValue(value);
    this.handleKeyup();
  }

  private getFormat() {
    if (this.format)
      return this.format;
    let places = this.decimalPlaces ?? null;
    if (places == null) {
      places = 2;
    }

    return `1.${places}-${places}`;
  }

  private stringToNumber(value: string | number): number {
    if(value === null || value === undefined)
      return NaN;
    if (typeof value === "string") {
      let asNumber = value.replace(/\s/g, '').replace('.', ',').replace(',', '.');
      return parseFloat(asNumber);
    }
    return value;
  }
}
